import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  
  const { token } = useParams();
  const navigate = useNavigate();

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.get(`https://phone-tracker-backend-wqeb.onrender.com/api/password/verify-token/${token}`);
        
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.user?.email || '');
        } else {
          setTokenValid(false);
          setError('Invalid or expired reset token');
        }
      } catch (err) {
        console.error('Token verification error:', err);
        setTokenValid(false);
        setError(err.response?.data?.message || 'Invalid or expired reset token');
      } finally {
        setCheckingToken(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
      setError('No reset token provided');
      setCheckingToken(false);
    }
  }, [token]);

  // Check password strength
  useEffect(() => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [password]);

  const getPasswordStrengthScore = () => {
    const strengths = Object.values(passwordStrength).filter(Boolean).length;
    if (strengths <= 2) return 'Weak';
    if (strengths <= 4) return 'Medium';
    return 'Strong';
  };

  const getPasswordStrengthColor = () => {
    const score = getPasswordStrengthScore();
    switch(score) {
      case 'Weak': return '#ff4444';
      case 'Medium': return '#ffaa44';
      case 'Strong': return '#00c853';
      default: return '#666';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    // Validation
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Check password strength
    const strengthScore = Object.values(passwordStrength).filter(Boolean).length;
    if (strengthScore < 4) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.put(`https://phone-tracker-backend-wqeb.onrender.com/api/password/reset-password/${token}`, {
        password,
        confirmPassword
      });

      setMessage(response.data.message || 'Password reset successful! Redirecting to login...');
      
      // Store token if auto-login is enabled
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('isLoggedIn', 'true');
        
        // Dispatch storage event for navigation update
        window.dispatchEvent(new Event('storage'));
      }
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(response.data.token ? '/dashboard' : '/login');
      }, 3000);

    } catch (err) {
      console.error('Reset password error:', err);
      if (err.response) {
        setError(err.response.data?.message || 'Failed to reset password');
      } else if (err.request) {
        setError('Cannot connect to server. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorContainer}>
            <span style={styles.errorIcon}>❌</span>
            <h2 style={styles.errorTitle}>Invalid or Expired Link</h2>
            <p style={styles.errorText}>
              {error || 'This password reset link is no longer valid. It may have expired or already been used.'}
            </p>
            <Link to="/forgot-password" style={styles.button}>
              Request New Reset Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const strengthScore = getPasswordStrengthScore();
  const strengthColor = getPasswordStrengthColor();

  return (
    <div style={styles.container}>
      {/* Background decoration */}
      <div style={styles.backgroundDecoration}>
        <div style={styles.decorationCircle1}></div>
        <div style={styles.decorationCircle2}></div>
      </div>

      {/* Reset Password Card */}
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>🔑</span>
          </div>
          <h2 style={styles.title}>Create New Password</h2>
          {userEmail && (
            <p style={styles.userEmail}>
              for <strong>{userEmail}</strong>
            </p>
          )}
          <p style={styles.subtitle}>
            Enter your new password below.
          </p>
        </div>

        {/* Success Message */}
        {message && (
          <div style={styles.successContainer}>
            <span style={styles.successIcon}>✅</span>
            <p style={styles.successText}>{message}</p>
          </div>
        )}

        {/* Error Message */}
        {error && !message && (
          <div style={styles.errorContainer}>
            <span style={styles.errorIcon}>⚠️</span>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Password Reset Form */}
        {!message && (
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Password Field */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🔒</span>
                New Password
              </label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={styles.passwordInput}
                  placeholder="Enter new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

            {/* Password Strength Meter */}
            {password && (
              <div style={styles.strengthContainer}>
                <div style={styles.strengthBar}>
                  <div style={{
                    ...styles.strengthFill,
                    width: `${(Object.values(passwordStrength).filter(Boolean).length / 5) * 100}%`,
                    backgroundColor: strengthColor
                  }}></div>
                </div>
                <p style={{...styles.strengthText, color: strengthColor}}>
                  Password Strength: {strengthScore}
                </p>
              </div>
            )}

            {/* Confirm Password Field */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>🔒</span>
                Confirm Password
              </label>
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    ...styles.passwordInput,
                    borderColor: confirmPassword && password !== confirmPassword ? '#ff4444' : '#e0e0e0'
                  }}
                  placeholder="Confirm new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.passwordToggle}
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p style={styles.matchError}>Passwords do not match</p>
              )}
            </div>

            {/* Password Requirements */}
            <div style={styles.requirementsContainer}>
              <p style={styles.requirementsTitle}>Password must contain:</p>
              <ul style={styles.requirementsList}>
                <li style={{color: passwordStrength.length ? '#00c853' : '#666'}}>
                  {passwordStrength.length ? '✓' : '○'} At least 8 characters
                </li>
                <li style={{color: passwordStrength.uppercase ? '#00c853' : '#666'}}>
                  {passwordStrength.uppercase ? '✓' : '○'} One uppercase letter
                </li>
                <li style={{color: passwordStrength.lowercase ? '#00c853' : '#666'}}>
                  {passwordStrength.lowercase ? '✓' : '○'} One lowercase letter
                </li>
                <li style={{color: passwordStrength.number ? '#00c853' : '#666'}}>
                  {passwordStrength.number ? '✓' : '○'} One number
                </li>
                <li style={{color: passwordStrength.special ? '#00c853' : '#666'}}>
                  {passwordStrength.special ? '✓' : '○'} One special character
                </li>
                <li style={{color: password && confirmPassword && password === confirmPassword ? '#00c853' : '#666'}}>
                  {password && confirmPassword && password === confirmPassword ? '✓' : '○'} Passwords match
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {})
              }}
              disabled={loading}
            >
              {loading ? (
                <span style={styles.buttonLoadingSpinner}>
                  <span style={styles.spinner}></span>
                  Resetting Password...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        {/* Back to Login Link */}
        <div style={styles.footer}>
          <Link to="/login" style={styles.backLink}>
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  backgroundDecoration: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  decorationCircle1: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    top: '-100px',
    right: '-100px',
    animation: 'float 6s ease-in-out infinite',
  },
  decorationCircle2: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    bottom: '-50px',
    left: '-50px',
    animation: 'float 8s ease-in-out infinite',
  },
  card: {
    position: 'relative',
    zIndex: 2,
    width: '450px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'slideUp 0.5s ease-out',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '40px',
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  logo: {
    marginBottom: '20px',
  },
  logoIcon: {
    fontSize: '48px',
  },
  title: {
    fontSize: '24px',
    color: '#333',
    marginBottom: '10px',
    fontWeight: '600',
  },
  userEmail: {
    color: '#667eea',
    fontSize: '14px',
    marginBottom: '10px',
  },
  subtitle: {
    color: '#666',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  successContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#e8f5e9',
    border: '1px solid #c8e6c9',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '20px',
    animation: 'slideUp 0.3s ease-out',
  },
  successIcon: {
    fontSize: '20px',
  },
  successText: {
    color: '#2e7d32',
    fontSize: '14px',
    margin: 0,
    flex: 1,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '40px',
  },
  errorText: {
    color: '#c00',
    fontSize: '14px',
    margin: 0,
    lineHeight: '1.6',
  },
  errorTitle: {
    color: '#c00',
    fontSize: '18px',
    marginBottom: '10px',
    fontWeight: '600',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  labelIcon: {
    fontSize: '16px',
  },
  passwordContainer: {
    position: 'relative',
    width: '100%',
  },
  passwordInput: {
    width: '100%',
    padding: '12px 45px 12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'all 0.3s',
    outline: 'none',
    boxSizing: 'border-box',
    ':focus': {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    padding: '5px',
    color: '#666',
    ':hover': {
      color: '#333',
    },
  },
  strengthContainer: {
    marginTop: '5px',
  },
  strengthBar: {
    height: '4px',
    backgroundColor: '#e0e0e0',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '4px',
  },
  strengthFill: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
  strengthText: {
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'right',
  },
  hintText: {
    color: '#ffaa44',
    fontSize: '12px',
    marginTop: '4px',
  },
  matchError: {
    color: '#ff4444',
    fontSize: '12px',
    marginTop: '4px',
  },
  requirementsContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    padding: '15px',
    marginTop: '10px',
  },
  requirementsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '10px',
  },
  requirementsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    fontSize: '13px',
    '& li': {
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginTop: '10px',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 20px rgba(102, 126, 234, 0.3)',
    },
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    ':hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
  buttonLoadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
  },
  footer: {
    marginTop: '25px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  backLink: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    ':hover': {
      textDecoration: 'underline',
    },
  },
};

// Add global styles for animations
const style = document.createElement('style');
style.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
  }
  
  button:hover:not(:disabled) {
    transform: translateY(-2px);
  }
`;
document.head.appendChild(style);

export default ResetPassword;